<template>
  <div style="width: 100%">
    <v-card flat :color="backgroundColor">
      <!--  Below div shows the flow if bookable is true -->
      <template v-if="selectedProduct.Bookable">
        <v-row class="ma-0">
          <!-- Selection of list, only for loggedin users -->
          <v-col class="pa-0"
            v-if="
              selectedProduct.Type !== 'Rental' &&
              selectedProduct.Type !== 'Virtual' &&
              userLoggedIn && !profView
            "
          >
            <v-select
              v-model="selectedShoppingList"
              :items="listItems"
              item-text="ShoppingListName"
              :label="$translate('PICK_A_LIST')"
              :disabled="!itemBookable"
              filled
              return-object
              hide-details="auto"
              @change="onListSelectionChange"
              dense
            />

            <v-text-field
              v-if="newShoppingListSelected"
              :label="$translate('SHOPPING_LIST_NAME')"
              v-model="nameInput"
              hide-details="auto"
            />
          </v-col>

          <v-col  class="pa-0">
            <v-btn v-if="!profView"
              color="secondary"
              depressed
              block
              width="100%"
              min-height="54"
              class="text-capitalize secondary-font-color"
              :disabled="bookBtnDisabled"
              @click="onBookBtnClick"
            >
              {{ bookBtnText }}
            </v-btn>
            <v-btn v-else
                   min-height="38"
                   color="secondary"
                   :disabled="bookBtnDisabled"
                   @click="onBookBtnClick"
            >
              <v-icon class="secondary-font-color" size="25px">mdi-plus</v-icon>
            </v-btn>
            <div
              v-if="newShoppingListSelected && nameInput === ''"
              class="py-3"
            >
              {{ $translate('TO_CONTINUE_PROVIDE_NAME') }}
            </div>
          </v-col>
        </v-row>
      </template>

      <!--  Below div shows the flow if bookable is false and user is not logged id -->
      <template v-else-if="!selectedProduct.Bookable && !userLoggedIn">
        <v-col>
          <v-alert outlined color="light-green" text>
            <v-row align="center">
              <v-col class="grow" v-html="selectedProduct.BookableText">
              </v-col>
              <v-col class="shrink">
                <v-btn
                  color="primary"
                  class="primary-font-color"
                  @click="onLoginBtnClick"
                >
                  {{ $translate('LOGIN') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>
        </v-col>
      </template>

      <template v-else>
        <v-col>
          <v-alert outlined color="light-green" text>
            <v-row align="center">
              <v-col class="grow" v-html="selectedProduct.BookableText">
                {{ selectedProduct.BookableText }}
              </v-col>
            </v-row>
          </v-alert>
        </v-col>
      </template>
    </v-card>
  </div>
</template>

<script>
import { SHOPPING_CONTAINER } from '@/shared/constants';
import { hasCapacity } from '@/objects/Product';

export default {
  name: 'SingleProductOrderAddToList',
  props: {
    selectedProduct: {
      type: Object,
      default: () => {},
    },
    selectedItem: {
      type: Object,
      default: () => {},
    },
    quantity: Number,
    backgroundColor: {
      type: String,
      default: 'transparent',
    },
    profView: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['book'],
  computed: {
    userLoggedIn() {
      return this.$store.getters['auth/isValid'];
    },
    currentSelectedShoppingList() {
      return this.$store.getters.getCurrentSelectedShoppingList;
    },
    shoppingLists() {
      const lists = this.$store.getters['shoppingList/orders'];
      if (lists == null || lists.length === 0) {
        return [];
      }

      const listsCopy = [];
      lists.forEach((list) => {
        listsCopy.push({
          ShoppingListId: list.ShoppingListId,
          ShoppingListName: list.ShoppingListName,
        });
      });

      return listsCopy;
    },
    listItems() {
      return [this.shoppingCart, ...this.shoppingLists, this.newShoppingList];
    },
    itemBookable() {
      if (
        this.selectedProduct.Bookable &&
        hasCapacity(this.selectedProduct, this.selectedItem) &&
        this.quantity > 0
      ) {
        return true;
      }

      return false;
    },
    newShoppingListSelected() {
      if (this.selectedShoppingList.ShoppingListId === this.newShoppingListId) {
        return true;
      }

      return false;
    },
    shoppingCartSelected() {
      if (this.selectedShoppingList.ShoppingListId === this.shoppingCartId) {
        return true;
      }

      return false;
    },
    bookBtnText() {
      return this.$translate('ADD');
    },
    bookBtnDisabled() {
      if (!this.itemBookable) {
        // Product not bookable
        return true;
      }

      if (
        this.selectedShoppingList == null ||
        this.selectedShoppingList.ShoppingListId == null
      ) {
        // Didn't pick a list
        return true;
      }

      if (this.newShoppingListSelected && this.nameInput === '') {
        // New shopping list selected but name not provided
        return true;
      }

      return false;
    },
  },
  watch: {
    currentSelectedShoppingList() {
      this.initListSelection();
    },
  },
  data() {
    return {
      nameInput: '',
      shoppingCartId: SHOPPING_CONTAINER.ShoppingCart,
      newShoppingListId: SHOPPING_CONTAINER.NewShoppingList,

      shoppingCart: {
        ShoppingListId: SHOPPING_CONTAINER.ShoppingCart,
        ShoppingListName: this.$translate('SHOPPING_CART_NAME'),
      },
      newShoppingList: {
        ShoppingListId: SHOPPING_CONTAINER.NewShoppingList,
        ShoppingListName: this.$translate('NEW_SHOPPING_LIST'),
      },
      selectedShoppingList: {},
    };
  },
  mounted() {
    this.initListSelection();
  },
  methods: {
    initListSelection() {
      // Init selected list
      if (
        this.selectedProduct.Type === 'Rental' ||
        this.selectedProduct.Type === 'Virtual' ||
        !this.userLoggedIn
      ) {
        // Shopping cart only
        this.selectedShoppingList = { ...this.shoppingCart };
      } else if (
        this.currentSelectedShoppingList != null &&
        this.currentSelectedShoppingList.ShoppingListId != null
      ) {
        this.selectedShoppingList = { ...this.currentSelectedShoppingList };
      } else {
        this.selectedShoppingList = { ...this.shoppingCart };
      }
    },
    onListSelectionChange() {
      // User changed the list selection, update selection in store
      if (
        this.selectedShoppingList.ShoppingListId !==
        this.newShoppingList.ShoppingListId
      ) {
        this.$store.commit(
          'setCurrentSelectedShoppingList',
          this.selectedShoppingList
        );
      }
    },
    onBookBtnClick() {
      const list = { ...this.selectedShoppingList };
      if (this.newShoppingListSelected) {
        list.ShoppingListName = this.nameInput;
      }

      this.$emit('book', list);
    },
    onLoginBtnClick() {
      this.$emit('login');
    },
  },
};
</script>
